import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, Users, DollarSign, FileText, 
  Settings, LogOut, Menu, X, ChevronDown, 
  UserCheck, Calendar, TrendingUp, Building2, Ticket, FileSearch, Award, TriangleAlert, Inbox, BookOpen
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsService } from '../../services';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Escape closes the mobile menu (keyboard users otherwise have to click the backdrop)
  useEffect(() => {
    if (!isSidebarOpen) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') setIsSidebarOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isSidebarOpen]);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [stats, setStats] = useState({
    totalAgents: 0,
    pendingApplications: 0,
    pendingCommissions: 0,
    monthlyRevenue: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await analyticsService.getDashboard();
      setStats({
        totalAgents: data.totalAgents || 0,
        pendingApplications: data.pendingApplications || 0,
        pendingCommissions: data.pendingCommissions || 0,
        monthlyRevenue: data.monthlyRevenue || 0
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Define all possible menu items with role requirements
  const allMenuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin', 'agency_admin', 'agents_manager', 'commissions_manager'] },
    { path: '/admin/agents', label: 'Agent Management', icon: Users, badge: stats.pendingApplications, roles: ['super_admin', 'agency_admin', 'agents_manager'] },
    { path: '/admin/benefits', label: 'Benefits', icon: Award, roles: ['agents_manager', 'agency_admin', 'super_admin'] },
    { path: '/admin/calendar', label: 'Calendar & Events', icon: Calendar, roles: ['super_admin', 'agency_admin', 'agents_manager'] },
    { path: '/admin/commissions', label: 'Commissions', icon: DollarSign, badge: stats.pendingCommissions, roles: ['super_admin', 'agency_admin', 'commissions_manager'] },
    { path: '/admin/remittances', label: 'Remittances', icon: Inbox, roles: ['super_admin', 'agency_admin'] },
    { path: '/admin/resources', label: 'Agent Resources', icon: BookOpen, roles: ['super_admin', 'agency_admin'] },
    { path: '/admin/vax-requests', label: 'Registration Requests', icon: Ticket, roles: ['agents_manager'], email: 'therese@parksavers.com' },
    { path: '/admin/support-tickets', label: 'Support Tickets', icon: Ticket, roles: ['super_admin', 'agency_admin'] },
    { path: '/admin/tax-forms', label: 'Tax Forms', icon: FileText, roles: ['super_admin', 'agency_admin'] },
    { path: '/admin/analytics', label: 'Analytics', icon: TrendingUp, roles: ['super_admin', 'agency_admin', 'agents_manager'] },
    { path: '/admin/system-logs', label: 'System Logs', icon: FileSearch, roles: ['super_admin'] },
    { path: '/admin/error-logs', label: 'Error Logs', icon: TriangleAlert, roles: ['super_admin'] },
    { path: '/admin/settings', label: 'Agency Settings', icon: Settings, roles: ['super_admin', 'agency_admin'] }
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => 
    item.roles.includes(user?.role) && (!item.email || item.email === user?.email?.toLowerCase())
  );

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden">
        {/* Mobile menu button - Enhanced */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors duration-200"
          aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
        >
          {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center">
              <Building2 className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Admin Portal</h2>
                <p className="text-xs text-gray-500">{user?.agency_name || 'Agency Admin'}</p>
              </div>
            </div>
          </div>

          {/* Navigation - Enhanced for mobile */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsSidebarOpen(false); // Close sidebar on mobile after navigation
                  }}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center flex-shrink-0">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* User profile */}
          <div className="border-t p-4">
            <div className="relative">
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {user?.name?.charAt(0).toUpperCase() || 'A'}
                  </div>
                  <div className="ml-3 text-left">
                    <p className="text-sm font-medium text-gray-900">{user?.name || 'Admin'}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                  isProfileDropdownOpen ? 'rotate-180' : ''
                }`} />
              </button>
              
              {isProfileDropdownOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    <span className="text-sm">Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between p-4 bg-white shadow-sm border-b">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors duration-200"
            aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900 truncate">
            {menuItems.find(item => isActive(item.path))?.label || 'Admin Panel'}
          </h1>
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>
        
        {/* Desktop header */}
        <div className="hidden lg:flex items-center justify-between p-4 bg-white shadow-sm border-b">
          <h1 className="text-2xl font-semibold text-gray-900">
            {menuItems.find(item => isActive(item.path))?.label || 'Admin Panel'}
          </h1>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{user?.name || 'Admin'}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>
        </div>
        
        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
